import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";

import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { DatePicker } from "@mui/x-date-pickers-v6";
import { LocalizationProvider } from "@mui/x-date-pickers-v6/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers-v6/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers-v6/AdapterDayjs";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import InfoIcon from "@mui/icons-material/Info";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { updateShippingOrder } from "src/api/shippingOrder";
import { getCityInfo, getQuotationInfo } from "src/api/lalamove";

import { useAuth } from "src/hooks/useAuth";
import font from "src/styles/font";
import device from "src/styles/device";
import {
  checkNotEmpty,
  convertIntToCurrency,
  convertPrice,
  currencyToNumber,
} from "src/common";
import {
  Black,
  DarkGrey,
  GreyStroke,
  Primary,
  White01,
  Yellow,
} from "src/styles/color";
import {
  serviceType,
  // categoriesText,
  // handlingInstructions,
} from "src/common/lalamove";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
// import Checkbox from "src/components/core/Checkbox";
import QuantityInput from "src/components/ui/QuantityInput";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import UploadImage from "src/components/upload/dropzone/UploadImage";
import CustomMap from "src/components/ui/Maps/CustomMaps";

import {
  SpanLabel,
  TextFieldDialog,
} from "src/modules/order/shipping-order/components";
import {
  MapsContainer,
  WrapperMapsContainer,
} from "src/components/order/components";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #d8d8dc;
  margin: 24px 0;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: ${({ slim }) => (slim ? "400" : "600")};
  line-height: 24px;
  margin: ${({ slim, unMargin }) => slim && !unMargin && "auto"};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const StyledSelect = styled(Select)`
  border-radius: 16px;

  .MuiSelect-select {
    padding: 12px 16px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }

  @media ${device.mobileL} {
    .MuiSelect-select {
      padding: 8px 12px;
    }
  }
`;

const SumPrice = styled.h2`
  color: ${Primary};
  margin: 0 16px 0 0;
  font-weight: 600;
`;

const Accordion = styled(MuiAccordion)`
  &.MuiAccordion-root {
    border: 1px solid #0000003b;
    box-shadow: none;
  }

  &.MuiPaper-root.MuiAccordion-root:last-of-type,
  &.MuiPaper-root.MuiAccordion-root:first-of-type {
    border-radius: 16px;
  }

  &::before {
    display: none;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  & .MuiAccordionSummary-content {
    font-size: ${font.size.md};
    font-weight: 400;
    color: #00000061;
  }

  &.MuiAccordionSummary-root.Mui-expanded {
    min-height: 60px;
  }
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 14px 0;
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.mobileL} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .MuiFormControl-root {
      width: 100%;
      min-width: unset;
      max-width: unset;
      min-height: 40px;
    }
  }
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${DarkGrey};
  font-size: ${({ size }) => (size === "title" ? font.size.sm : font.size.xs)};
  font-weight: ${({ size }) => (size === "title" ? 500 : 400)};

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const RemarkText = styled.span`
  font-size: ${font.size.xs};
  font-weight: 400;
  color: ${Black};
`;

const WrapperCalOrder = styled.div`
  /* width: 100%; */
  background-color: ${White01};
  padding: 16px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  gap: 16px;
  /* border-bottom: 1px solid #d9d9d9; */
`;

const RowSumPrice = styled.div`
  margin-left: 0;
  margin-right: 0;
  justify-content: space-between;
  align-items: center;
  display: flex;
`;

const Price = styled.span`
  color: ${(props) => (props.fontColor ? props.fontColor : "#252525")};
  font-size: 1.125rem;
  font-weight: 400;

  ${({ desc, total }) =>
    desc && total
      ? css`
          font-size: 1rem;
          font-weight: 600;
        `
      : desc
        ? css`
            font-size: 1rem;
          `
        : total
          ? css`
              font-weight: 500;
              color: ${Primary};
            `
          : null}
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }
`;

const WrapperUploadImage = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapperImgSlip = styled.div`
  width: 100%;
  max-width: 300px;
  height: auto;
`;

const ImgSlip = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ConfirmDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  setIsLoading,
  isEdit = false,
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const isAdmin = user?.role_id === 1;

  const initialDetail = {
    order: {},
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: 13.6940782,
            lng: 100.6022862,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
        lalamoveCheck: {
          request: null,
          result: null,
        },
        lalamoveRequest: {
          request: null,
          result: null,
        },
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          customerQtFile: null,
          customerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          customerInvFile: null,
          customerInvAdditionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          xsurfacePoFile: null,
          xsurfacePoAdditionalFile: null,
        },
        vc: {
          vcDate: null,
          vcNo: null,
          xsurfaceVcFile: null,
          xsurfaceVcAdditionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          sellerQtFile: null,
          sellerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          sellerInvFile: null,
          sellerInvAdditionalFile: null,
        },
      },
    },
  };
  const [shippingDetail, setShippingDetail] = useState(initialDetail);
  const [isProductIsReady, setIsProductIsReady] = useState("ready");

  // lalamove
  const [selectedCities, setSelectedCities] = useState([]);

  const initialQuotation = {
    orderId: orderDetail?.order?._id || null,
    scheduleAt: null,
    serviceType: null,
    specialRequests: [], // optional
    language: "th_TH",
    stops: [
      {
        name: "",
        phone: "",
        coordinates: {
          lat: 13.6940782,
          lng: 100.6022862,
        },
        address: "",
        remark: "", // remove before send request
      },
    ],
    // รายการของที่ซื้อ
    item: {
      quantity: 1,
      weight: null,
      categories: [],
      handlingInstructions: [],
    },
    isRouteOptimized: true,
  };
  const [quotationPayload, setQuotationPayload] = useState(initialQuotation);
  const [isQuotationChanged, setIsQuotationChanged] = useState(false); // for check if quotation is changed

  const removeServiceType = [
    "MOTORCYCLE",
    "MOTORCYCLE_RENTAL",
    "MPV_INTERCITY",
    "SEDAN_INTERCITY",
    "SUV_INTERCITY",
    "LD_PICKUP_TRUCK",
    "LD_4_DOOR_PICKUP_TRUCK",
  ];
  // end

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail((order) => ({
        ...order,
        ...orderDetail,
        order: {
          ...orderDetail.order,
        },
        method: orderDetail.method
          ? isJSONString(orderDetail.method)
          : "ส่งไปถึงลูกค้าโดยตรง",
        fulfillmentProcess:
          orderDetail.fulfillmentProcess.length > 0
            ? orderDetail.fulfillmentProcess
            : [
                ...initialDetail.fulfillmentProcess.map((fulfill) => {
                  if (fulfill.carrier && typeof fulfill.carrier === "string") {
                    fulfill.carrier =
                      orderDetail.order?.deliType === "lalamove-xsf"
                        ? "Lalamove ส่งฟรี"
                        : "จัดส่งโดยร้านค้า";
                  }
                  if (fulfill.type && typeof fulfill.type === "string") {
                    fulfill.type =
                      orderDetail.order?.deliType === "lalamove-xsf"
                        ? "โดย Xsurface"
                        : "มีค่าจัดส่ง";
                  } else fulfill.shippingFee = fulfill.shippingFee || "0";
                  return fulfill;
                }),
              ],
        // initialDetail.fulfillmentProcess,
        payment: orderDetail.payment,
        documents: {
          ...order.documents,
          ...orderDetail.documents,
        },
      }));

      if (orderDetail.order?.deliType === "lalamove-xsf") {
        setQuotationPayload((prev) => ({
          ...prev,
          stops: [
            {
              ...prev.stops[0],
              name:
                orderDetail?.order?.productChoiceOrders[0]?.productChoiceOrder
                  ?.product?.user?.shipping_address?.[0]?.name || "",
              phone:
                orderDetail?.order?.productChoiceOrders[0]?.productChoiceOrder
                  ?.product?.user?.shipping_address?.[0]?.tel || "",
              address:
                orderDetail?.order?.productChoiceOrders[0]?.productChoiceOrder
                  ?.product?.user?.shipping_address?.[0]?.address || "",
              coordinates: {
                lat:
                  orderDetail?.order?.productChoiceOrders[0]?.productChoiceOrder
                    ?.product?.user?.shipping_address?.[0]?.coordinates?.lat ||
                  13.6940782,
                lng:
                  orderDetail?.order?.productChoiceOrders[0]?.productChoiceOrder
                    ?.product?.user?.shipping_address?.[0]?.coordinates?.lng ||
                  100.6022862,
              },
            },
          ],
        }));
      }

      fetchCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const deliveryMethods = [
    "ส่งไปถึงลูกค้าโดยตรง",
    "ส่งไปที่ขนส่ง",
    "ลูกค้ารับเอง",
  ];

  const carriers = ["จัดส่งโดยร้านค้า", "Lalamove", "อื่นๆ"];

  const deliveryFee = ["มีค่าจัดส่ง", "ไม่มีค่าจัดส่ง", "เก็บเงินปลายทาง"];
  const multipleDeliveryFee = ["มีค่าจัดส่ง", "ไม่มีค่าจัดส่ง"];

  const renameFile = (file) => {
    const randomPart = Math.floor(Math.random() * 100000);
    let newName = `QTSL${dayjs().format("DDMMYY")}-${randomPart}`;
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const parseCurrency = (str) => {
    return parseFloat(str.replace(/,/g, ""));
  };

  const fetchCities = async () => {
    try {
      setIsLoading(true);
      const { data: res } = await getCityInfo();
      if (!!res) {
        // setCitiesData(res);
        let bangkokData = res.find((item) => item?.name === "Bangkok");
        setSelectedCities({
          ...bangkokData,
          services: bangkokData?.services?.filter(
            (item) => !removeServiceType.includes(item?.key),
          ),
        });
      }
    } catch (error) {
      console.error("err: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   *
   * @note - this function is used to check if the quotation is changed
   * @param {*} oldValue - key of value in shippingDetail.fulfillmentProcess[0].lalamoveCheck.request to check
   * @param {*} changedValue - new value to compare with oldValue
   */
  const checkIsQuotationChange = (oldValue, changedValue) => {
    if (
      !!shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.request &&
      changedValue !== oldValue
    ) {
      setIsQuotationChanged(true);
    } else {
      setIsQuotationChanged(false);
    }
  };

  const onGetQuotation = async () => {
    try {
      setIsLoading(true);
      const { scheduleAt, ...payload } = quotationPayload;
      const { weight, ...item } = payload?.item;
      const quoInfo = {
        ...payload,
        ...(!!scheduleAt ? { scheduleAt } : {}),
        serviceType: payload.serviceType.key,
        item: {
          ...item,
          ...(!!weight ? { weight } : {}),
          quantity: item?.quantity.toString(),
        },
        stops: payload.stops.map(({ remark = "", ...stop }) => ({
          ...stop,
          name: `${stop.name}${remark ? ` (${remark})` : ""}`,
          phone: stop.phone.replaceAll("-", "").replace(stop.phone[0], "+66"),
          coordinates: {
            lat: stop.coordinates.lat.toString(),
            lng: stop.coordinates.lng.toString(),
          },
        })),
      };

      const { data: res } = await getQuotationInfo({ data: quoInfo });

      if (!!res) {
        // setIsQuotationChanged(false);

        setShippingDetail((prev) => {
          const updateShipping = [...prev.fulfillmentProcess];
          updateShipping[0] = {
            ...updateShipping[0],
            ...{
              lalamoveCheck: {
                request: res?.request,
                result: res?.result,
              },
            },
          };
          return {
            ...prev,
            fulfillmentProcess: updateShipping,
          };
        });

        setIsQuotationChanged(false);
      }
    } catch (error) {
      console.error(error?.response?.data || error);

      error.response.data.errors.forEach((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
          autoHideDuration: 2000,
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (result) => {
    try {
      setIsLoading(true);
      const payload = {
        ...shippingDetail,
        fulfillmentProcess: [
          ...shippingDetail.fulfillmentProcess.map((fulfill) => {
            if (fulfill.shippingFee && typeof fulfill.shippingFee === "string")
              fulfill.shippingFee = parseCurrency(fulfill.shippingFee);
            else fulfill.shippingFee = fulfill.shippingFee || "0";
            return fulfill;
          }),
        ],
        status: {
          value:
            isProductIsReady === "ready"
              ? isEdit
                ? shippingDetail?.status?.value
                : "confirmed"
              : "failed",
          failureReason:
            isProductIsReady === "ready"
              ? null
              : shippingDetail.status.failureReason,
        },
        order: orderDetail?.order?._id,
      };
      delete payload.orderDetail;

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      let document = isJSONString(data.get("documents"));
      delete document.seller.qt.sellerQtFile;
      delete document.seller.qt.sellerQtAdditionalFile;
      data.set("documents", JSON.stringify(document));

      if (
        typeof payload.documents.seller.qt.sellerQtFile === "object" &&
        payload.documents.seller.qt.sellerQtFile !== null
      ) {
        if (payload.documents.seller.qt.sellerQtFile.path) {
          data.append(
            "documents.seller.qt.sellerQtFile",
            renameFile(payload.documents.seller.qt.sellerQtFile),
          );
        } else {
          document.seller.qt.sellerQtFile =
            payload.documents.seller.qt.sellerQtFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }
      if (
        typeof payload.documents.seller.qt.sellerQtAdditionalFile ===
          "object" &&
        payload.documents.seller.qt.sellerQtAdditionalFile !== null
      ) {
        if (payload.documents.seller.qt.sellerQtAdditionalFile.path) {
          data.append(
            "documents.seller.qt.sellerQtAdditionalFile",
            renameFile(payload.documents.seller.qt.sellerQtAdditionalFile),
          );
        } else {
          document.seller.qt.sellerQtAdditionalFile =
            payload.documents.seller.qt.sellerQtAdditionalFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }

      await updateShippingOrder(shippingDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`อัพเดทรายละเอียดการจัดส่งสินค้า สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error("err:", err);
      enqueueSnackbar(err?.response?.data?.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const ShippingFeeApplies = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>
            {`ค่าจัดส่ง (บาท)`} <span>*</span>
          </SpanLabel>
          <NumericFormat
            customInput={TextField}
            variant="outlined"
            thousandSeparator=","
            decimalSeparator="."
            allowNegative={false}
            decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
            sx={{
              width: "100%",
              "& input": {
                textAlign: "right",
              },
            }}
            value={shippingDetail?.fulfillmentProcess[index]?.shippingFee}
            onChange={({ target }) => {
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ shippingFee: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              });
            }}
          />
        </Grid>

        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <TextField
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={shippingDetail?.fulfillmentProcess[index]?.remark}
            onChange={({ target }) =>
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ remark: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              })
            }
          />
        </Grid>

        <Grid
          item
          container
          xs={12}
          mt={3}
          mb={0}
          justifyContent={"space-between"}
        >
          <Grid item display="flex" alignItems="center">
            <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
          </Grid>

          <Grid item display="flex" justifyContent="center">
            <SumPrice>
              {shippingDetail?.fulfillmentProcess[0].type === "มีค่าจัดส่ง"
                ? shippingDetail?.fulfillmentProcess.length > 1
                  ? convertIntToCurrency(
                      currencyToNumber(
                        shippingDetail?.fulfillmentProcess[0]?.shippingFee,
                      ) +
                        currencyToNumber(
                          shippingDetail?.fulfillmentProcess[1]?.shippingFee,
                        ),
                    )
                  : convertIntToCurrency(
                      shippingDetail?.fulfillmentProcess[0]?.shippingFee,
                    )
                : convertIntToCurrency(
                    shippingDetail?.fulfillmentProcess[1]?.shippingFee ?? 0,
                  )}
            </SumPrice>
            <SpanTitle slim>บาท</SpanTitle>
          </Grid>
        </Grid>

        <Line />

        <Grid item container xs={12} justifyContent="space-between">
          <Grid item xs={12} mb={2}>
            <SpanTitle>ช่องทางการชำระเงิน</SpanTitle>
          </Grid>

          <Grid item xs={5.8}>
            <SpanLabel isMargin>
              ชื่อบัญชี <span>*</span>
            </SpanLabel>
            <TextField
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.payment?.info?.bankAccountName}
              onChange={({ target }) => {
                const newShipping = shippingDetail;
                newShipping.payment.info.bankAccountName = target.value;
                setShippingDetail({
                  ...newShipping,
                });
              }}
            />
          </Grid>

          <Grid item xs={5.8}>
            <SpanLabel isMargin>
              เลขบัญชี <span>*</span>
            </SpanLabel>
            <TextField
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.payment.info.bankNo}
              onChange={({ target }) => {
                const newShipping = shippingDetail;
                newShipping.payment.info.bankNo = target.value;
                setShippingDetail({
                  ...newShipping,
                });
              }}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const FreeShipping = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <TextField
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={shippingDetail?.fulfillmentProcess[index]?.remark}
            onChange={({ target }) =>
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ remark: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              })
            }
          />
        </Grid>
      </>
    );
  };

  const CashOnDelivery = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>
            {`ค่าจัดส่งโดยประมาณ (บาท)`} <span>*</span>
          </SpanLabel>
          <NumericFormat
            customInput={TextField}
            variant="outlined"
            thousandSeparator=","
            decimalSeparator="."
            allowNegative={false}
            decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
            sx={{
              width: "100%",
              "& input": {
                textAlign: "right",
              },
            }}
            value={shippingDetail?.fulfillmentProcess[index]?.shippingFee}
            onChange={({ target }) => {
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ shippingFee: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              });
            }}
          />
        </Grid>

        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <TextField
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={shippingDetail?.fulfillmentProcess[index]?.remark}
            onChange={({ target }) =>
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ remark: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              })
            }
          />
        </Grid>

        {shippingDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" ||
        shippingDetail?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง" ? (
          <Grid
            item
            container
            xs={12}
            mt={3}
            mb={0}
            justifyContent={"space-between"}
          >
            <Grid item display="flex" alignItems="center">
              <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
            </Grid>

            <Grid item display="flex" justifyContent="center">
              <SumPrice>
                {shippingDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                  ? shippingDetail?.fulfillmentProcess.length > 1
                    ? shippingDetail?.fulfillmentProcess[1]?.type ===
                      "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          currencyToNumber(
                            shippingDetail?.fulfillmentProcess[0].shippingFee,
                          ) +
                            currencyToNumber(
                              shippingDetail?.fulfillmentProcess[1].shippingFee,
                            ),
                        )
                      : convertIntToCurrency(
                          +shippingDetail?.fulfillmentProcess[0].shippingFee,
                        )
                    : convertIntToCurrency(
                        +shippingDetail?.fulfillmentProcess[0].shippingFee,
                      )
                  : shippingDetail?.fulfillmentProcess.length > 1
                    ? shippingDetail?.fulfillmentProcess[1]?.type ===
                      "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          +shippingDetail?.fulfillmentProcess[1].shippingFee,
                        )
                      : 0
                    : 0}
              </SumPrice>
              <SpanTitle slim>บาท</SpanTitle>
            </Grid>
          </Grid>
        ) : null}
      </>
    );
  };

  const FreeLalamoveShipping = () => {
    return (
      <>
        <Grid item xs={12}>
          <Grid container spacing={2} sx={{ py: 2 }}>
            {/* Recipient Location */}
            <Grid item xs={12}>
              <SpanLabel isMargin>ที่อยู่จัดส่ง (ปลายทาง)</SpanLabel>

              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="recipient-content"
                  id="recipient-header"
                >
                  {/* Recipient Address */}
                  {shippingDetail?.order?.shippingAddress?.recipients?.address}
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Recipient Coordinates */}
                    <Grid item xs={12}>
                      <WrapperMapsContainer>
                        <MapsContainer>
                          <CustomMap
                            markerLocation={{
                              lat: shippingDetail?.order?.shippingAddress
                                ?.recipients?.coordinates?.lat,
                              lng: shippingDetail?.order?.shippingAddress
                                ?.recipients?.coordinates?.lng,
                            }}
                            isViewOnly
                          />
                        </MapsContainer>
                      </WrapperMapsContainer>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <SpanLabel>
                จุดรับสินค้า (คลังสินค้า)<span>*</span>
              </SpanLabel>
            </Grid>

            {/* Sender Coordinates */}
            <Grid item xs={12}>
              <WrapperMapsContainer>
                <MapsContainer>
                  <CustomMap
                    markerLocation={{
                      lat: quotationPayload?.stops[0]?.coordinates?.lat,
                      lng: quotationPayload?.stops[0]?.coordinates?.lng,
                    }}
                    changeMarkerLocation={(value) => {
                      let stopData = quotationPayload.stops;
                      stopData[0].coordinates = value;
                      setQuotationPayload({
                        ...quotationPayload,
                        stops: stopData,
                      });

                      setIsQuotationChanged(true);
                    }}
                    changeSeletedAddress={(value) => {
                      let stopData = quotationPayload.stops;
                      stopData[0].address = value;
                      setQuotationPayload({
                        ...quotationPayload,
                        stops: stopData,
                      });
                    }}
                    inputPlaceholder="จุดรับสินค้า (คลังสินค้า)"
                  />
                </MapsContainer>
              </WrapperMapsContainer>
            </Grid>

            {/* Sender Address */}
            <Grid item xs={12}>
              <TextField
                label="ที่อยู่คลังสินค้าสำหรับเข้ารับสินค้า"
                variant="outlined"
                fullWidth
                value={quotationPayload?.stops[0]?.address}
                onChange={(e) => {
                  let stopData = quotationPayload.stops;
                  stopData[0].address = e.target.value;
                  setQuotationPayload({
                    ...quotationPayload,
                    stops: stopData,
                  });

                  checkIsQuotationChange(
                    shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                      ?.request?.stops[0]?.address,
                    e.target.value,
                  );
                }}
                error={!!!quotationPayload?.stops[0]?.address}
                helperText={
                  !!!quotationPayload?.stops[0]?.address
                    ? `กรุณากรอกที่อยู่`
                    : ``
                }
              />
            </Grid>

            {/* Sender Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="ชื่อผู้ส่ง"
                variant="outlined"
                fullWidth
                value={quotationPayload?.stops[0]?.name}
                onChange={(e) => {
                  let stopData = quotationPayload.stops;
                  stopData[0].name = e.target.value;
                  setQuotationPayload({
                    ...quotationPayload,
                    stops: stopData,
                  });
                }}
              />
            </Grid>

            {/* Sender Phone */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="เบอร์โทรผู้ส่ง"
                placeholder="เฉพาะตัวเลข 10 หลัก"
                variant="outlined"
                fullWidth
                value={quotationPayload?.stops[0]?.phone}
                onChange={(e) => {
                  let stopData = quotationPayload.stops;
                  stopData[0].phone = e.target.value;
                  setQuotationPayload({
                    ...quotationPayload,
                    stops: stopData,
                  });
                }}
              />
            </Grid>

            {/* Remark */}
            <Grid item xs={12}>
              <TextField
                id="remark-input"
                label="หมายเหตุถึงคนขับ"
                placeholder="หมายเหตุถึงคนขับ (ไม่บังคับ)"
                fullWidth
                inputProps={{ maxLength: 30 }}
                onChange={(e) => {
                  let stopData = quotationPayload.stops;
                  stopData[0].remark = e.target.value;
                  setQuotationPayload({
                    ...quotationPayload,
                    stops: stopData,
                  });
                }}
                value={quotationPayload?.stops[0]?.remark}
                helperText="Max 30 characters"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                // freeSolo
                fullWidth
                aria-required={true}
                id="service-type-select"
                getOptionSelected={(option, value) =>
                  option?.key === value?.key
                }
                isOptionEqualToValue={(option, value) =>
                  option?.key === value?.key
                }
                options={
                  selectedCities?.services?.filter(
                    (item) => item.key !== "LD_4_DOOR_PICKUP_TRUCK",
                  ) || selectedCities?.services
                }
                value={quotationPayload?.serviceType}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={`service-type-select-${option?.key}`}>
                    <Box
                      sx={{
                        width: "100%",
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        // justifyContent: "space-around",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <img
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "8px",
                          }}
                          src={`https://xsurface-images.s3.ap-southeast-1.amazonaws.com/lalamove-icon/${option?.key}.png`}
                          alt={`${option?.key}-img`}
                        />
                      </Box>

                      <Box>
                        <TextOption size="title">
                          {serviceType(option?.key)}
                        </TextOption>

                        <TextOption>
                          {`ขนาดสินค้าแนะนำ : ${option?.dimensions?.length?.value} x ${option?.dimensions?.width?.value} x ${option?.dimensions?.height?.value} เมตร`}
                        </TextOption>
                        <TextOption>
                          {`น้ำหนักสูงสุด ${option?.load?.value} กิโลกรัม`}
                        </TextOption>
                      </Box>
                    </Box>
                  </li>
                )}
                onChange={(_, newValue) => {
                  if (!!newValue) {
                    setQuotationPayload({
                      ...quotationPayload,
                      serviceType: newValue,
                      specialRequests: [],
                    });
                    checkIsQuotationChange(
                      shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                        ?.request?.serviceType,
                      newValue.key,
                    );
                  } else {
                    setQuotationPayload({
                      ...quotationPayload,
                      serviceType: null,
                      specialRequests: [],
                    });
                    setIsQuotationChanged(true);
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                    py: "3px",
                  },
                }}
                getOptionLabel={(option) => serviceType(option?.key)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="เลือกประเภทรถ"
                    error={!quotationPayload?.serviceType}
                  />
                )}
              />
              <Stack
                alignItems="center"
                direction="row"
                // justifyContent="center"
                spacing={1}
                sx={{ mt: 1 }}
              >
                <InfoIcon style={{ color: Yellow }} />
                <RemarkText>กรุณาเลือกประเภทรถให้เข้ากับสินค้า</RemarkText>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="เวลาเข้ารับสินค้า"
                  format="DD/MM/YYYY - hh:mm A"
                  value={
                    !!quotationPayload?.scheduleAt
                      ? dayjs(quotationPayload?.scheduleAt)
                      : null
                  }
                  onChange={(newValue) => {
                    let selectedDate = new Date(newValue.$d);
                    setQuotationPayload({
                      ...quotationPayload,
                      scheduleAt: selectedDate.toISOString(),
                    });
                    checkIsQuotationChange(
                      shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                        ?.request?.scheduleAt,
                      null,
                    );
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-input.MuiOutlinedInput-input": {
                      padding: "12px 16px",
                    },
                    "& .MuiFormLabel-root.MuiInputLabel-root[data-shrink=false]:not(.Mui-focused)":
                      {
                        transform: "translate(14px, 12px) scale(1)",
                      },
                  }}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      error: !quotationPayload?.scheduleAt,
                      helperText: !quotationPayload?.scheduleAt
                        ? "กรุณาเลือกวันที่ที่ต้องการจัดส่งสินค้า"
                        : "",
                    },
                  }}
                  minDateTime={dayjs(new Date())}
                  maxDate={dayjs(new Date()).add(7, "day")}
                  shouldDisableDate={(day) =>
                    dayjs(day).day() === 0 || dayjs(day).day() === 6
                  }
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>

        {/* Product delivered detail */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* volume */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={3}>
                <SpanLabel>
                  จำนวนพัสดุ<span>*</span>
                </SpanLabel>

                <QuantityInput
                  onChange={(value) => {
                    let itemDetail = quotationPayload?.item;
                    itemDetail.quantity = value;
                    setQuotationPayload({
                      ...quotationPayload,
                      item: itemDetail,
                    });

                    checkIsQuotationChange(
                      Number(
                        shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                          ?.request?.item?.quantity,
                      ),
                      value,
                    );
                  }}
                  value={quotationPayload?.item?.quantity}
                />
              </Stack>
            </Grid>

            {/* get quotation */}
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Button
                variant="secondary"
                // size="large"
                disabled={
                  !quotationPayload?.scheduleAt ||
                  !quotationPayload?.serviceType
                }
                onClick={() => {
                  onGetQuotation();
                }}
                sx={{
                  minWidth: 160,
                  width: "max-content",
                }}
                startIcon={<LocalShippingIcon />}
              >
                ตรวจสอบรายการ
              </Button>

              {!!shippingDetail.fulfillmentProcess[0]?.lalamoveCheck &&
                isQuotationChanged && (
                  <FormHelperText
                    error={true}
                    sx={{ textAlign: "center", mt: 2 }}
                  >
                    ** รายละเอียดการเข้ารับสินค้ามีการเปลี่ยนแปลง
                    กรุณาตรวจสอบรายการใหม่อีกครั้ง **
                  </FormHelperText>
                )}
            </Grid>

            {!isQuotationChanged &&
            !!shippingDetail.fulfillmentProcess[0]?.lalamoveCheck?.result ? (
              <>
                <Grid item xs={12}>
                  <WrapperCalOrder>
                    <RowSumPrice>
                      <Price desc>ค่าจัดส่ง</Price>
                      <Price>
                        {convertPrice(
                          Number(
                            shippingDetail.fulfillmentProcess[0]?.lalamoveCheck
                              ?.result?.totalWithFee,
                          ),
                        )}
                      </Price>
                    </RowSumPrice>
                  </WrapperCalOrder>
                </Grid>
              </>
            ) : null}
          </Grid>
        </Grid>
      </>
    );
  };

  const QuotationDocument = () => {
    return (
      <>
        <Line />

        <Grid container xs={12} mb={2}>
          <SpanTitle>เอกสาร</SpanTitle>
        </Grid>

        <Grid container mb={2}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
          >
            <SpanLabel isMargin>วันที่ใบเสนอราคา</SpanLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                inputFormat="DD-MM-YYYY"
                value={
                  !!shippingDetail?.documents?.seller?.qt?.qtDate
                    ? dayjs(shippingDetail?.documents?.seller?.qt?.qtDate)
                    : null
                }
                adapterLocale="th"
                onChange={(value) => {
                  setShippingDetail((prevState) => {
                    const updateShipping = prevState;
                    updateShipping.documents.seller.qt.qtDate =
                      value !== null ? value.$d : "";
                    return {
                      ...prevState,
                      ...updateShipping,
                    };
                  });
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-input.MuiOutlinedInput-input": {
                    padding: "12px 16px",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      height: {
                        xs: 40,
                        sm: 48,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            sx={{ pl: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
          >
            <SpanLabel isMargin>{`เลขที่ใบเสนอราคา`}</SpanLabel>
            <TextFieldDialog
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.documents?.seller?.qt?.qtNo}
              onChange={({ target }) =>
                setShippingDetail((prevState) => {
                  const updateShipping = prevState;
                  updateShipping.documents.seller.qt.qtNo = target.value;
                  return {
                    ...prevState,
                    ...updateShipping,
                  };
                })
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12}>
          <Grid item xs={12}>
            <SpanLabel isMargin>{`ใบเสนอราคา (ของร้านค้า)`}</SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {/* additionalFile */}
                    {checkNotEmpty(
                      shippingDetail?.documents?.seller?.qt?.sellerQtFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.seller.qt.sellerQtFile = "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtFile =
                            fileCallBlack;

                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.seller?.qt?.sellerQtFile !==
                "object"
                  ? shippingDetail?.documents?.seller?.qt?.sellerQtFile
                  : !!shippingDetail?.documents?.seller?.qt?.sellerQtFile
                        ?.docUrl
                    ? shippingDetail?.documents?.seller?.qt?.sellerQtFile
                        ?.originalFileName
                    : (shippingDetail?.documents?.seller?.qt?.sellerQtFile
                        ?.name ??
                      shippingDetail?.documents?.seller?.qt?.sellerQtFile
                        ?.docUrl)
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12} mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.seller?.qt
                        ?.sellerQtAdditionalFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.seller.qt.sellerQtAdditionalFile =
                            {};
                          shippingDetail.documents.seller.qt.additionalNo = "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtAdditionalFile =
                            fileCallBlack;
                          newShipping.documents.seller.qt.additionalNo =
                            fileCallBlack.name;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.seller?.qt
                  ?.sellerQtAdditionalFile !== "object"
                  ? shippingDetail?.documents?.seller?.qt
                      ?.sellerQtAdditionalFile
                  : shippingDetail?.documents?.seller?.qt?.additionalNo
              }
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const validateField = () => {
    if (isProductIsReady === "ready") {
      if (shippingDetail?.method === "ส่งไปถึงลูกค้าโดยตรง") {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.carrier ===
            "จัดส่งโดยร้านค้า" ||
          shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove" ||
          shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove ส่งฟรี"
        ) {
          if (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง") {
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.shippingFee &&
              shippingDetail?.payment?.info?.bankAccountName &&
              shippingDetail?.payment?.info?.bankNo
              // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
            )
              return false;
            else return true;
          } else if (
            shippingDetail?.fulfillmentProcess?.[0]?.type === "เก็บเงินปลายทาง"
          ) {
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.shippingFee
              // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
            )
              return false;
            else return true;
          } else if (
            shippingDetail?.fulfillmentProcess?.[0]?.type === "โดย Xsurface"
          ) {
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.lalamoveCheck?.result
              // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
            )
              return false;
            else return true;
          } else {
            // return !shippingDetail?.documents?.seller?.qt?.sellerQtFile;
            return false;
          }
        } else {
          if (shippingDetail?.fulfillmentProcess?.[0]?.carrierAdditional)
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง"
            ) {
              if (
                shippingDetail?.fulfillmentProcess?.[0]?.shippingFee &&
                shippingDetail?.payment?.info?.bankAccountName &&
                shippingDetail?.payment?.info?.bankNo
                // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
              )
                return false;
              else return true;
            } else if (
              shippingDetail?.fulfillmentProcess?.[0]?.type ===
              "เก็บเงินปลายทาง"
            ) {
              if (
                shippingDetail?.fulfillmentProcess?.[0]?.shippingFee
                // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
              )
                return false;
              else return true;
            } else {
              // return !shippingDetail?.documents?.seller?.qt?.sellerQtFile;
              return false;
            }
          else return true;
        }
      } else if (shippingDetail?.method === "ส่งไปที่ขนส่ง") {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.carrier ===
            "จัดส่งโดยร้านค้า" ||
          shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove"
        ) {
          if (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง") {
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.shippingFee &&
              shippingDetail?.fulfillmentProcess?.[1]?.carrierAdditional &&
              shippingDetail?.fulfillmentProcess?.[1]?.shippingFee &&
              shippingDetail?.payment?.info?.bankAccountName &&
              shippingDetail?.payment?.info?.bankNo
              // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
            ) {
              return false;
            } else return true;
          } else if (
            shippingDetail?.fulfillmentProcess?.[0]?.type === "เก็บเงินปลายทาง"
          ) {
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.shippingFee &&
              shippingDetail?.fulfillmentProcess?.[1]?.carrierAdditional &&
              shippingDetail?.fulfillmentProcess?.[1]?.shippingFee
              // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
            )
              return false;
            else return true;
          } else {
            if (
              // shippingDetail?.documents?.seller?.qt?.sellerQtFile &&
              shippingDetail?.fulfillmentProcess?.[1]?.carrierAdditional &&
              shippingDetail?.fulfillmentProcess?.[1]?.shippingFee
            )
              return false;
            else return true;
          }
        } else {
          if (shippingDetail?.fulfillmentProcess?.[0]?.carrierAdditional)
            if (
              shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง"
            ) {
              if (
                shippingDetail?.fulfillmentProcess?.[0]?.shippingFee &&
                shippingDetail?.payment?.info?.bankAccountName &&
                shippingDetail?.payment?.info?.bankNo
                // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
              )
                return false;
              else return true;
            } else if (
              shippingDetail?.fulfillmentProcess?.[0]?.type ===
              "เก็บเงินปลายทาง"
            ) {
              if (
                shippingDetail?.fulfillmentProcess?.[0]?.shippingFee
                // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
              )
                return false;
              else return true;
            } else {
              // return !shippingDetail?.documents?.seller?.qt?.sellerQtFile;
              return false;
            }
          else return true;
        }
      } else {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.pickupAddress?.coordinates
            ?.lat &&
          shippingDetail?.fulfillmentProcess?.[0]?.pickupAddress?.coordinates
            ?.lng &&
          shippingDetail?.fulfillmentProcess?.[0]?.pickupAddress?.tel &&
          shippingDetail?.fulfillmentProcess?.[0]?.pickupAddress?.address
          // && shippingDetail?.documents?.seller?.qt?.sellerQtFile
        )
          return false;
        else return true;
      }
    } else {
      if (shippingDetail?.status?.failureReason) return false;
      return true;
    }
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        {isEdit
          ? "แก้ไขรายละเอียดการจัดส่งสินค้า"
          : "รายละเอียดการจัดส่งสินค้า"}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <FormControl>
          <RadioGroup
            row
            name="row-radio-buttons-group"
            value={isProductIsReady}
            defaultValue={isProductIsReady}
            onChange={({ target }) => {
              setIsProductIsReady(target.value);
              setShippingDetail(initialDetail);
            }}
          >
            <FormControlLabel
              value="ready"
              control={
                <Radio
                  sx={{
                    color: "#D8D8DC",
                    padding: "5px",
                    "&.Mui-checked": {
                      color: "#E13B30",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 25,
                    },
                  }}
                />
              }
              label="มีสินค้าพร้อมจัดส่ง"
            />
            <FormControlLabel
              value="not_ready"
              control={
                <Radio
                  sx={{
                    color: "#D8D8DC",
                    padding: "5px",
                    "&.Mui-checked": {
                      color: "#E13B30",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 25,
                    },
                  }}
                />
              }
              label="สินค้าไม่พร้อมจัดส่ง"
            />
          </RadioGroup>
        </FormControl>

        {isProductIsReady === "ready" ? (
          <>
            <Line />

            <Grid container xs={12} mb={2}>
              <SpanTitle>การจัดส่งสินค้า</SpanTitle>
            </Grid>

            <Grid container xs={12}>
              <Grid item xs={12}>
                <SpanLabel isMargin>
                  รูปแบบการจัดส่ง <span>*</span>
                </SpanLabel>
                <StyledSelect
                  fullWidth
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 4,
                        ".MuiList-root": {
                          padding: 0,
                        },
                        ".MuiMenuItem-root": {
                          minHeight: {
                            sm: 48,
                            xs: 40,
                          },
                          px: {
                            sm: 2,
                            xs: 1.5,
                          },
                          py: {
                            sm: 1.5,
                            xs: 1,
                          },
                          fontSize: {
                            sm: font.size.md,
                            xs: font.size.sm,
                          },
                        },
                      },
                    },
                  }}
                  value={shippingDetail?.method}
                  onChange={({ target }) => {
                    setShippingDetail((prevState) => {
                      const fulfillmentProcess =
                        shippingDetail.fulfillmentProcess;
                      if (target.value === "ส่งไปที่ขนส่ง") {
                        let obj = {
                          ...fulfillmentProcess[0],
                          carrier: "อื่นๆ",
                          type: "มีค่าจัดส่ง",
                          shippingFee: null,
                        };
                        fulfillmentProcess.push(obj);
                      } else {
                        if (fulfillmentProcess.length > 1)
                          fulfillmentProcess.pop();
                      }
                      return {
                        ...prevState,
                        method: target.value,
                        fulfillmentProcess: [...fulfillmentProcess],
                        payment: {
                          ...initialDetail.payment,
                        },
                      };
                    });
                  }}
                  disabled={shippingDetail?.order?.deliType === "lalamove-xsf"}
                >
                  {deliveryMethods.map((option) => (
                    <MenuItem value={option}>{option}</MenuItem>
                  ))}
                </StyledSelect>
              </Grid>

              {shippingDetail?.method === "ส่งไปถึงลูกค้าโดยตรง" && (
                <>
                  <Grid item xs={12} container mt={2} columnSpacing={2}>
                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[0]?.carrier ===
                        "อื่นๆ"
                          ? 4.2
                          : 8
                      }
                    >
                      <SpanLabel isMargin>
                        ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          value={shippingDetail?.fulfillmentProcess[0]?.carrier}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{ carrier: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                          disabled={
                            shippingDetail?.order?.deliType === "lalamove-xsf"
                          }
                        >
                          {shippingDetail?.order?.deliType ===
                          "lalamove-xsf" ? (
                            <MenuItem value={"Lalamove ส่งฟรี"}>
                              Lalamove ส่งฟรี
                            </MenuItem>
                          ) : (
                            carriers.map((option, index) => (
                              <MenuItem value={option} key={index}>
                                {option}
                              </MenuItem>
                            ))
                          )}
                        </StyledSelect>
                      </FormControl>
                    </Grid>

                    {shippingDetail?.fulfillmentProcess[0]?.carrier ===
                      "อื่นๆ" && (
                      <Grid
                        item
                        xs={
                          shippingDetail?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      >
                        <SpanLabel isMargin>
                          ชื่อขนส่งที่ใช้บริการ <span>*</span>
                        </SpanLabel>

                        <TextField
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={
                            shippingDetail?.fulfillmentProcess[0]
                              ?.carrierAdditional
                          }
                          onChange={({ target }) =>
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{ carrierAdditional: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            })
                          }
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[0]?.carrier ===
                        "อื่นๆ"
                          ? 3.6
                          : 4
                      }
                    >
                      <SpanLabel isMargin>
                        ค่าจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          value={shippingDetail?.fulfillmentProcess[0]?.type}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                shippingFee: null,
                                ...{ type: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                                payment: {
                                  ...initialDetail.payment,
                                  info: {
                                    bankAccountName: "",
                                    bankNo: "",
                                  },
                                },
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                          disabled={
                            shippingDetail?.order?.deliType === "lalamove-xsf"
                          }
                        >
                          {shippingDetail?.order?.deliType ===
                          "lalamove-xsf" ? (
                            <MenuItem value={"โดย Xsurface"}>
                              โดย Xsurface
                            </MenuItem>
                          ) : (
                            deliveryFee.map((option, index) => (
                              <MenuItem value={option} key={index}>
                                {option}
                              </MenuItem>
                            ))
                          )}
                        </StyledSelect>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "มีค่าจัดส่ง" && ShippingFeeApplies()}

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "ไม่มีค่าจัดส่ง" && FreeShipping()}

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "เก็บเงินปลายทาง" && CashOnDelivery()}

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "โดย Xsurface" && FreeLalamoveShipping()}

                  {!isEdit && QuotationDocument()}
                </>
              )}

              {shippingDetail?.method === "ส่งไปที่ขนส่ง" && (
                <>
                  <Grid item xs={12} mt={2}>
                    <SpanLabel
                      highlight
                    >{`การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง)`}</SpanLabel>
                  </Grid>

                  <Grid item xs={12} container mt={2} columnSpacing={2}>
                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[0]?.carrier ===
                        "อื่นๆ"
                          ? 4.2
                          : 8
                      }
                    >
                      <SpanLabel isMargin>
                        ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          labelId="status-select-label"
                          id="status-select"
                          value={shippingDetail?.fulfillmentProcess[0]?.carrier}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{ carrier: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                        >
                          {carriers.map((option) => (
                            <MenuItem value={option}>{option}</MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                    </Grid>

                    {shippingDetail?.fulfillmentProcess[0]?.carrier ===
                      "อื่นๆ" && (
                      <Grid
                        item
                        xs={
                          shippingDetail?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      >
                        <SpanLabel isMargin>
                          ชื่อขนส่งที่ใช้บริการ <span>*</span>
                        </SpanLabel>

                        <TextField
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={
                            shippingDetail?.fulfillmentProcess[0]
                              ?.carrierAdditional
                          }
                          onChange={({ target }) =>
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{ carrierAdditional: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            })
                          }
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[0]?.carrier ===
                        "อื่นๆ"
                          ? 3.6
                          : 4
                      }
                    >
                      <SpanLabel isMargin>
                        ค่าจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          labelId="status-select-label"
                          id="status-select"
                          value={shippingDetail?.fulfillmentProcess[0]?.type}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                shippingFee: null,
                                ...{ type: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                                payment: {
                                  ...initialDetail.payment,
                                  info: {
                                    bankAccountName: "",
                                    bankNo: "",
                                  },
                                },
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                        >
                          {multipleDeliveryFee.map((option) => (
                            <MenuItem value={option}>{option}</MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {shippingDetail?.fulfillmentProcess[0].type ===
                    "มีค่าจัดส่ง" && (
                    <Grid item xs={12} mt={2}>
                      <SpanLabel isMargin>
                        {`ค่าจัดส่ง (บาท)`} <span>*</span>
                      </SpanLabel>
                      <NumericFormat
                        customInput={TextField}
                        variant="outlined"
                        thousandSeparator=","
                        decimalSeparator="."
                        allowNegative={false}
                        decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                        sx={{
                          width: "100%",
                          "& input": {
                            textAlign: "right",
                          },
                        }}
                        value={
                          shippingDetail?.fulfillmentProcess[0]?.shippingFee
                        }
                        onChange={({ target }) => {
                          setShippingDetail((prevState) => {
                            const updateShipping = [
                              ...prevState.fulfillmentProcess,
                            ];
                            updateShipping[0] = {
                              ...updateShipping[0],
                              ...{ shippingFee: target.value },
                            };
                            return {
                              ...prevState,
                              fulfillmentProcess: updateShipping,
                            };
                          });
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} mt={2}>
                    <SpanLabel
                      highlight
                    >{`การส่งของที่ 2 (ขนส่งส่งไปลูกค้า)`}</SpanLabel>
                  </Grid>

                  <Grid item xs={12} container mt={2} columnSpacing={2}>
                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[1]?.carrier ===
                        "อื่นๆ"
                          ? 4.2
                          : 8
                      }
                    >
                      <SpanLabel isMargin>
                        ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          labelId="status-select-label"
                          id="status-select"
                          value={shippingDetail?.fulfillmentProcess[1]?.carrier}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[1] = {
                                ...updateShipping[1],
                                ...{ carrier: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="อื่นๆ">{`อื่นๆ`}</MenuItem>
                          {/* {carriers.map((option) => (
                            // <MenuItem value={option}>{option}</MenuItem>
                          ))} */}
                        </StyledSelect>
                      </FormControl>
                    </Grid>

                    {shippingDetail?.fulfillmentProcess[1]?.carrier ===
                      "อื่นๆ" && (
                      <Grid
                        item
                        xs={
                          shippingDetail?.fulfillmentProcess[1]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      >
                        <SpanLabel isMargin>
                          ชื่อขนส่งที่ใช้บริการ <span>*</span>
                        </SpanLabel>

                        <TextField
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={
                            shippingDetail?.fulfillmentProcess[1]
                              ?.carrierAdditional
                          }
                          onChange={({ target }) =>
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[1] = {
                                ...updateShipping[1],
                                ...{ carrierAdditional: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            })
                          }
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={
                        shippingDetail?.fulfillmentProcess[1]?.carrier ===
                        "อื่นๆ"
                          ? 3.6
                          : 4
                      }
                    >
                      <SpanLabel isMargin>
                        ค่าจัดส่ง <span>*</span>
                      </SpanLabel>

                      <FormControl sx={{ width: "100%" }}>
                        <StyledSelect
                          labelId="status-select-label"
                          id="status-select"
                          value={shippingDetail?.fulfillmentProcess[1]?.type}
                          onChange={({ target }) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[1] = {
                                ...updateShipping[1],
                                shippingFee: null,
                                ...{ type: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                                payment: {
                                  ...initialDetail.payment,
                                  info: {
                                    bankAccountName: "",
                                    bankNo: "",
                                  },
                                },
                              };
                            });
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                        >
                          {["มีค่าจัดส่ง", "เก็บเงินปลายทาง"].map((option) => (
                            <MenuItem value={option}>{option}</MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "มีค่าจัดส่ง" ||
                  shippingDetail?.fulfillmentProcess[1]?.type ===
                    "มีค่าจัดส่ง" ? (
                    <>
                      {shippingDetail?.fulfillmentProcess[1]?.type ===
                      "มีค่าจัดส่ง" ? (
                        <Grid item xs={12} mt={2}>
                          <SpanLabel isMargin>
                            {`ค่าจัดส่ง (บาท)`} <span>*</span>
                          </SpanLabel>
                          <NumericFormat
                            customInput={TextField}
                            variant="outlined"
                            thousandSeparator=","
                            decimalSeparator="."
                            allowNegative={false}
                            decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                            sx={{
                              width: "100%",
                              "& input": {
                                textAlign: "right",
                              },
                            }}
                            value={
                              shippingDetail?.fulfillmentProcess[1]?.shippingFee
                            }
                            onChange={({ target }) => {
                              setShippingDetail((prevState) => {
                                const updateShipping = [
                                  ...prevState.fulfillmentProcess,
                                ];
                                updateShipping[1] = {
                                  ...updateShipping[1],
                                  ...{ shippingFee: target.value },
                                };
                                return {
                                  ...prevState,
                                  fulfillmentProcess: updateShipping,
                                };
                              });
                            }}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12} mt={2}>
                          <SpanLabel isMargin>
                            {`ค่าจัดส่งโดยประมาณ (บาท)`} <span>*</span>
                          </SpanLabel>
                          <NumericFormat
                            customInput={TextField}
                            variant="outlined"
                            thousandSeparator=","
                            decimalSeparator="."
                            allowNegative={false}
                            decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                            sx={{
                              width: "100%",
                              "& input": {
                                textAlign: "right",
                              },
                            }}
                            value={
                              shippingDetail?.fulfillmentProcess[1]?.shippingFee
                            }
                            onChange={({ target }) => {
                              setShippingDetail((prevState) => {
                                const updateShipping = [
                                  ...prevState.fulfillmentProcess,
                                ];
                                updateShipping[1] = {
                                  ...updateShipping[1],
                                  ...{ shippingFee: target.value },
                                };
                                return {
                                  ...prevState,
                                  fulfillmentProcess: updateShipping,
                                };
                              });
                            }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} mt={2}>
                        <SpanLabel isMargin>หมายเหตุ</SpanLabel>
                        <TextField
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={shippingDetail?.fulfillmentProcess[1]?.remark}
                          onChange={({ target }) =>
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[1] = {
                                ...updateShipping[1],
                                ...{ remark: target.value },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            })
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        container
                        mt={3}
                        mb={0}
                        justifyContent={"space-between"}
                      >
                        <Grid item display="flex" alignItems="center">
                          <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
                        </Grid>
                        <Grid item display="flex" justifyContent="center">
                          <SumPrice>
                            {shippingDetail?.fulfillmentProcess[0].type ===
                            "มีค่าจัดส่ง"
                              ? shippingDetail?.fulfillmentProcess.length > 1
                                ? shippingDetail?.fulfillmentProcess[1].type ===
                                  "มีค่าจัดส่ง"
                                  ? convertIntToCurrency(
                                      currencyToNumber(
                                        shippingDetail?.fulfillmentProcess[0]
                                          .shippingFee,
                                      ) +
                                        currencyToNumber(
                                          shippingDetail?.fulfillmentProcess[1]
                                            .shippingFee,
                                        ),
                                    )
                                  : convertIntToCurrency(
                                      shippingDetail?.fulfillmentProcess[0]
                                        .shippingFee,
                                    )
                                : convertIntToCurrency(
                                    shippingDetail?.fulfillmentProcess[0]
                                      .shippingFee,
                                  )
                              : convertIntToCurrency(
                                  shippingDetail?.fulfillmentProcess[1]
                                    .shippingFee ?? 0,
                                )}
                          </SumPrice>
                          <SpanTitle slim>บาท</SpanTitle>
                        </Grid>
                      </Grid>

                      <Line />

                      <Grid container xs={12} justifyContent="space-between">
                        <Grid container xs={12} mb={2}>
                          <SpanTitle>ช่องทางการชำระเงิน</SpanTitle>
                        </Grid>

                        <Grid item xs={5.8}>
                          <SpanLabel isMargin>
                            ชื่อบัญชี <span>*</span>
                          </SpanLabel>
                          <TextField
                            variant="outlined"
                            fullWidth
                            value={
                              shippingDetail?.payment?.info?.bankAccountName
                            }
                            onChange={({ target }) => {
                              const newShipping = shippingDetail;
                              newShipping.payment.info.bankAccountName =
                                target.value;
                              setShippingDetail({
                                ...newShipping,
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={5.8}>
                          <SpanLabel isMargin>
                            เลขบัญชี <span>*</span>
                          </SpanLabel>
                          <TextField
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={shippingDetail?.payment.info.bankNo}
                            onChange={({ target }) => {
                              const newShipping = shippingDetail;
                              newShipping.payment.info.bankNo = target.value;
                              setShippingDetail({
                                ...newShipping,
                              });
                            }}
                          />
                        </Grid>

                        {!isEdit && QuotationDocument()}
                      </Grid>
                    </>
                  ) : null}

                  {shippingDetail?.fulfillmentProcess[0]?.type ===
                    "ไม่มีค่าจัดส่ง" &&
                    shippingDetail?.fulfillmentProcess[1]?.type ===
                      "เก็บเงินปลายทาง" &&
                    CashOnDelivery(1)}
                </>
              )}

              {shippingDetail?.method === "ลูกค้ารับเอง" && (
                <>
                  <Grid container xs={12} mt={2}>
                    <SpanLabel isMargin>
                      ปักหมุด Location <span>*</span>
                    </SpanLabel>
                    <WrapperMapsContainer>
                      <MapsContainer>
                        <CustomMap
                          // markerLocation={{
                          //   lat: shippingDetail?.fulfillmentProcess[0]
                          //     ?.pickupAddress?.coordinates?.lat,
                          //   lng: shippingDetail?.fulfillmentProcess[0]
                          //     ?.pickupAddress?.coordinates?.lng,
                          // }}
                          changeMarkerLocation={(value) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{
                                  pickupAddress: {
                                    ...updateShipping[0].pickupAddress,
                                    coordinates: value,
                                  },
                                },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            });
                          }}
                          changeSeletedAddress={(value) => {
                            setShippingDetail((prevState) => {
                              const updateShipping = [
                                ...prevState.fulfillmentProcess,
                              ];
                              updateShipping[0] = {
                                ...updateShipping[0],
                                ...{
                                  pickupAddress: {
                                    ...updateShipping[0].pickupAddress,
                                    address: value,
                                  },
                                },
                              };
                              return {
                                ...prevState,
                                fulfillmentProcess: updateShipping,
                              };
                            });
                          }}
                          inputPlaceholder="ตำแหน่งที่ตั้งร้านค้า *"
                        />
                      </MapsContainer>
                    </WrapperMapsContainer>
                  </Grid>

                  <Grid item xs={12} mt={2}>
                    <SpanLabel isMargin>
                      ที่อยู่ร้านค้า <span>*</span>
                    </SpanLabel>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={
                        shippingDetail?.fulfillmentProcess[0]?.pickupAddress
                          ?.address
                      }
                      onChange={({ target }) => {
                        setShippingDetail((prevState) => {
                          const updateShipping = [
                            ...prevState.fulfillmentProcess,
                          ];
                          updateShipping[0] = {
                            ...updateShipping[0],
                            ...{
                              pickupAddress: {
                                ...updateShipping[0].pickupAddress,
                                address: target.value,
                              },
                            },
                          };
                          return {
                            ...prevState,
                            fulfillmentProcess: updateShipping,
                          };
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} mt={2}>
                    <SpanLabel isMargin>
                      เบอร์ติดต่อ <span>*</span>
                    </SpanLabel>
                    <TextField
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={
                        shippingDetail?.fulfillmentProcess[0]?.pickupAddress
                          ?.tel
                      }
                      onChange={({ target }) =>
                        setShippingDetail((prevState) => {
                          const updateShipping = [
                            ...prevState.fulfillmentProcess,
                          ];
                          updateShipping[0] = {
                            ...updateShipping[0],
                            ...{
                              pickupAddress: {
                                ...updateShipping[0].pickupAddress,
                                tel: target.value,
                              },
                            },
                          };
                          return {
                            ...prevState,
                            fulfillmentProcess: updateShipping,
                          };
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} mt={2}>
                    <SpanLabel isMargin>หมายเหตุ</SpanLabel>
                    <TextField
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={shippingDetail?.fulfillmentProcess[0]?.remark}
                      onChange={({ target }) =>
                        setShippingDetail((prevState) => {
                          const updateShipping = [
                            ...prevState.fulfillmentProcess,
                          ];
                          updateShipping[0] = {
                            ...updateShipping[0],
                            ...{ remark: target.value },
                          };
                          return {
                            ...prevState,
                            fulfillmentProcess: updateShipping,
                          };
                        })
                      }
                    />
                  </Grid>

                  {!isEdit && QuotationDocument()}
                </>
              )}
            </Grid>
          </>
        ) : (
          <Grid xs={12} mt={3}>
            <SpanLabel>
              สาเหตุที่ไม่พร้อมส่ง <span>*</span>
            </SpanLabel>
            <TextField
              value={shippingDetail?.status?.failureReason}
              fullWidth
              multiline
              rows={4.2}
              onChange={({ target }) =>
                setShippingDetail((prev) => ({
                  ...prev,
                  status: {
                    ...prev.status,
                    failureReason: target.value,
                  },
                }))
              }
            />
          </Grid>
        )}

        {isAdmin &&
          isEdit &&
          (shippingDetail?.status?.value === "preparing" ||
            shippingDetail?.status?.value === "delivering") && (
            <>
              <Line />
              <Grid item xs={12} mt={2}>
                <WrapperBox>
                  <SpanTitle slim unMargin>
                    วันที่ชำระเงิน *
                  </SpanTitle>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledDatePicker
                      inputFormat="DD-MM-YYYY"
                      adapterLocale="th"
                      value={
                        !!shippingDetail?.payment?.paymentDate
                          ? dayjs(shippingDetail?.payment?.paymentDate)
                          : null
                      }
                      onChange={(value) => {
                        const newValue = shippingDetail;
                        newValue.payment.paymentDate = value.$d;
                        setShippingDetail({ ...newValue });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          sx={{
                            // my: 2,
                            maxWidth: 200,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </WrapperBox>
              </Grid>

              <Grid container xs={12} mt={3} mb={5}>
                <UploadImage
                  callBackFile={(fileCallBlack) => {
                    if (fileCallBlack !== "") {
                      const newValue = shippingDetail;
                      newValue.payment.paymentProof = fileCallBlack;
                      setShippingDetail({ ...newValue });
                    }
                  }}
                  closeAlert={false}
                >
                  {checkNotEmpty(shippingDetail?.payment?.paymentProof) ? (
                    <WrapperUploadImage>
                      <WrapperImgSlip>
                        <ImgSlip
                          src={shippingDetail?.payment?.paymentProof.docUrl}
                          alt="paySlipImage"
                        />
                      </WrapperImgSlip>
                    </WrapperUploadImage>
                  ) : (
                    <WrapperUploadImage>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AddIcon />}
                      >
                        แนบหลักฐานการชำระเงิน
                      </Button>
                    </WrapperUploadImage>
                  )}
                </UploadImage>

                {checkNotEmpty(shippingDetail?.payment?.paymentProof) && (
                  <Grid container xs={12} justifyContent="center" mt={2}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<SaveAltIcon />}
                      onClick={() => {
                        const fileUrl =
                          shippingDetail?.payment?.paymentProof.docUrl;
                        const link = document.createElement("a");
                        link.href = fileUrl;
                        link.setAttribute("target", "_blank"); // เปิดในแท็บใหม่
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      ดาวน์โหลด
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>
          )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          fullWidth
          sx={{ maxWidth: 180 }}
          type="submit"
          disabled={validateField()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ConfirmDialog;
